import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

const VARIANTS = {
  amber: 'bg-amber-500 text-white',
  blue: 'bg-blue-600 text-white',
  green: 'bg-emerald-500 text-white',
};

/**
 * Minimal drop-down toast pill — slides from the top, auto-hides, dismissible.
 * Used for offline mode + BookingFlows Pro membership notices. Informational
 * only: it NEVER blocks or locks the UI underneath.
 */
export const DropToast = ({ show, variant = 'amber', autoHideMs, onDismiss, testId, children }) => {
  const [visible, setVisible] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (show) {
      timer.current = setTimeout(() => setVisible(true), 30);
      if (autoHideMs) {
        const hide = setTimeout(() => setVisible(false), autoHideMs);
        return () => { clearTimeout(timer.current); clearTimeout(hide); };
      }
    } else {
      setVisible(false);
    }
    return () => clearTimeout(timer.current);
  }, [show, autoHideMs]);

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 z-[9999] flex justify-center pointer-events-none px-4" style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}>
      <div
        data-testid={testId}
        className={`pointer-events-auto flex items-center gap-2.5 rounded-full pl-4 pr-1.5 py-1.5 shadow-lg text-xs font-medium max-w-[92vw] transition-[transform,opacity] duration-500 ease-out ${VARIANTS[variant] || VARIANTS.amber} ${visible ? 'translate-y-0 opacity-100' : '-translate-y-24 opacity-0'}`}
      >
        {children}
        <button
          onClick={() => { setVisible(false); if (onDismiss) setTimeout(onDismiss, 250); }}
          className="p-1.5 rounded-full hover:bg-white/20 transition-colors shrink-0"
          data-testid={testId ? `${testId}-dismiss` : undefined}
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default DropToast;
